import React, { useCallback, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router";
import ContentOverviewReports from "features/Reporting/Content/ContentOverviewReports";
import { ReportingPeople } from "features/Reporting/People/ReportingPeople";
import { AddOnIds } from "enums";
import { ButtonGroup } from "components/buttonGroup/ButtonGroup";

import styles from "features/Reporting/Content/contentOverviewReports.module.scss";
import localStyles from "./performanceReportsWrapper.module.scss";
import { bindAction } from "interfaces/redux";
import { fetchAddOns } from "features/Accounts/state/thunks/addOnsThunk";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import cn from "classnames";

type PerformanceReportsWrapperProps = {
  childAccountId: number;
} & PropsFromRedux;

type ReportOption = {
  label: string;
  path: string;
  isActive: boolean;
};

const PerformanceReportsWrapper: React.FC<PerformanceReportsWrapperProps> = ({
  childAccountId,
  addOns,
  fetchAddOns,
  accountId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const basePath = useMemo(() => {
    const pathParts = location.pathname.split("/");
    const performanceIndex = pathParts.indexOf("performance");
    if (performanceIndex !== -1) {
      return pathParts.slice(0, performanceIndex + 1).join("/");
    }
    return location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    fetchAddOns(accountId);
  }, [accountId, fetchAddOns]);

  const hasCreateAccountsAddOn = useMemo(
    () =>
      Array.isArray(addOns?.items) &&
      addOns.items.some((addOn) => addOn.id === AddOnIds.CreateAccounts && addOn.isEnabled),
    [addOns],
  );

  const reportOptions: ReportOption[] = useMemo(
    () => [
      {
        label: "People",
        path: `${basePath}/people`,
        isActive: location.pathname.includes("/people"),
      },
      {
        label: "Content",
        path: `${basePath}/content`,
        isActive: location.pathname.includes("/content"),
      },
    ],
    [basePath, location.pathname],
  );

  const currentReport = useMemo(() => reportOptions.find((opt) => opt.isActive) ?? reportOptions[0], [reportOptions]);

  const reportLabels = useMemo(() => reportOptions.map((opt) => opt.label), [reportOptions]);

  const handleReportChange = useCallback(
    (label: string) => {
      const option = reportOptions.find((opt) => opt.label === label);
      if (option) {
        navigate(option.path);
      }
    },
    [reportOptions, navigate],
  );

  return (
    <div className={cn(styles.performanceWrapper, localStyles.root)}>
      <div className={styles.reportToggleHeader}>
        <div className={localStyles.reportToggleHeader}>
          <ButtonGroup
            items={reportLabels}
            defaultValue={currentReport?.label ?? reportLabels[0]}
            onChange={handleReportChange}
          />
        </div>
      </div>
      <Routes>
        <Route
          path="content/*"
          element={
            <ContentOverviewReports
              childAccountId={childAccountId}
              hasCreateAccountsAddOn={hasCreateAccountsAddOn}
              usesDropdownMenuNavigation
            />
          }
        />
        <Route
          path="people/*"
          element={
            <ReportingPeople
              childAccountId={childAccountId}
              hasCreateAccountsAddOn={hasCreateAccountsAddOn}
              usesDropdownMenuNavigation
            />
          }
        />
        <Route index element={<Navigate to={"./people"} replace />} />
      </Routes>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  addOns: state.accounts.addOns,
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchAddOns: bindAction(fetchAddOns, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PerformanceReportsWrapper);
export default ConnectedComponent;
